import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { Shield, Save, Check, X, ChevronDown, Copy, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/constants';
import { useNotification } from '../contexts/NotificationContext';

/**
 * AddRole — standalone page to create a brand-new role with permissions.
 * Features:
 *   - Fetches all existing roles dynamically for the "Copy from" dropdown
 *   - Selecting a role pre-fills the permissions (as a starting template)
 *   - Full permission grid for fine-grained control
 *
 * Props:
 *   onSuccess()  — called after the role is successfully created
 *   onCancel()   — called when the user hits Cancel
 */
const AddRole = ({ onSuccess, onCancel }) => {
    const { userRole } = useContext(AuthContext);
    const { showNotification } = useNotification();

    // Form state
    const [formData, setFormData] = useState({ name: '', description: '', permissions: [] });
    const [isSaving, setIsSaving] = useState(false);

    // Dynamic roles list for the "Copy from" dropdown
    const [existingRoles, setExistingRoles] = useState([]);
    const [rolesLoading, setRolesLoading] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState(''); // role_id of selected template or ''

    // ── Fetch existing roles on mount ────────────────────────────────────────
    useEffect(() => {
        fetchExistingRoles();
    }, []);

    const fetchExistingRoles = async () => {
        setRolesLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/get_roles`, { withCredentials: true });
            if (response.data.status === 'success') {
                setExistingRoles(response.data.data || []);
            } else {
                setExistingRoles([]);
            }
        } catch (error) {
            console.error('Error fetching roles for template dropdown:', error);
            setExistingRoles([]);
        } finally {
            setRolesLoading(false);
        }
    };

    // ── Handle "Copy from" dropdown change ──────────────────────────────────
    const handleTemplateChange = (e) => {
        const val = e.target.value;
        setSelectedTemplate(val);

        if (!val) {
            // "-- Start fresh --" selected: clear permissions only, keep name
            setFormData(prev => ({ ...prev, permissions: [] }));
            return;
        }

        const sourceRole = existingRoles.find(r => String(r.role_id) === String(val));
        if (sourceRole) {
            // Pre-fill permissions from selected role
            // Name stays editable — user can type their own name
            setFormData(prev => ({
                ...prev,
                permissions: Array.isArray(sourceRole.permissions) ? [...sourceRole.permissions] : []
            }));
            showNotification('info',
                `Permissions copied from "${sourceRole.name}". You can now adjust them and enter a new role name.`
            );
        }
    };

    // ── Permission structure ─────────────────────────────────────────────────
    const permissionStructure = [
        {
            category: 'DASHBOARD',
            subsections: [{ name: 'DASHBOARD', key: 'Dashboard', actions: ['View'] }]
        },
        {
            category: 'USER & ACCESS MANAGEMENT',
            subsections: [
                { name: 'USERS', key: 'Users', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'ROLES', key: 'Roles', actions: ['View', 'Add', 'Edit', 'Delete'] }
            ]
        },
        {
            category: 'PATIENT MANAGEMENT',
            subsections: [
                { name: 'PATIENTS', key: 'Patients', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'PATIENT FEEDBACK', key: 'Patient Feedback', actions: ['View', 'Add', 'Edit', 'Delete'] }
            ]
        },
        {
            category: 'PRE-OPERATIVE',
            subsections: [
                { name: 'OT BOOKING', key: 'OT Booking', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'PRE-OP CHECKLIST', key: 'Pre-Op Checklist', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'PRE-OP ORDERS', key: 'Pre-Op Orders', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'PRE-ANAESTHETIC EVALUATION', key: 'Pre-Anaesthetic Evaluation', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'CONSENTS', key: 'Consents', actions: ['View', 'Add', 'Edit', 'Delete'] }
            ]
        },
        {
            category: 'INTRA-OPERATIVE',
            subsections: [
                { name: 'OPERATION SHEET', key: 'Operation Sheet', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'NURSING RECORD', key: 'Nursing Record', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'SURGICAL SAFETY', key: 'Surgical Safety Checklist', actions: ['View', 'Add', 'Edit', 'Delete'] }
            ]
        },
        {
            category: 'POST-OPERATIVE',
            subsections: [
                { name: 'POST-OP ORDERS', key: 'Post-Op Orders', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'POST-OP HANDOVER', key: 'Post-Op Handover', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'ALDERETE SCORE', key: 'Alderete Score', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'POST-OP INSTRUCTIONS', key: 'Post-Op Instructions', actions: ['View', 'Add', 'Edit', 'Delete'] }
            ]
        },
        {
            category: 'INVENTORY & SETTINGS',
            subsections: [
                { name: 'INVENTORY', key: 'Inventory', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'OT ROOMS', key: 'OT Rooms', actions: ['View', 'Add', 'Edit', 'Delete'] }
            ]
        }
    ];

    const flatPermissions = [];
    permissionStructure.forEach(cat => {
        cat.subsections.forEach(sub => {
            sub.actions.forEach(action => {
                flatPermissions.push(`${sub.key} ${action}`);
            });
        });
    });

    // ── Permission helpers ───────────────────────────────────────────────────
    const togglePermission = (permKey) => {
        setFormData(prev => ({
            ...prev,
            permissions: prev.permissions.includes(permKey)
                ? prev.permissions.filter(p => p !== permKey)
                : [...prev.permissions, permKey]
        }));
    };

    const handleQuickAction = (actionType) => {
        setFormData(prev => {
            let newPerms = [...prev.permissions];
            if (actionType === 'Check All') {
                newPerms = [...flatPermissions];
            } else if (actionType === 'Uncheck All') {
                newPerms = [];
            } else {
                const allOfSuffix = flatPermissions.filter(p => p.endsWith(actionType));
                const allSelected = allOfSuffix.every(p => prev.permissions.includes(p));
                if (allSelected) {
                    newPerms = newPerms.filter(p => !p.endsWith(actionType));
                } else {
                    allOfSuffix.forEach(p => { if (!newPerms.includes(p)) newPerms.push(p); });
                }
            }
            return { ...prev, permissions: newPerms };
        });
    };

    // ── Validation ───────────────────────────────────────────────────────────
    const validate = () => {
        if (!formData.name.trim()) {
            showNotification('error', 'Role name cannot be empty. Please enter a name for this role.');
            return false;
        }
        if (formData.name.trim().length < 3) {
            showNotification('error', 'Role name must be at least 3 characters long.');
            return false;
        }
        return true;
    };

    // ── Save ─────────────────────────────────────────────────────────────────
    const handleSave = async (e) => {
        if (e) e.preventDefault();
        if (!validate()) return;

        setIsSaving(true);
        const payload = {
            name: formData.name.trim(),
            description: formData.description.trim(),
            permissions: formData.permissions
        };

        try {
            await axios.post(`${API_BASE_URL}/add_role`, payload, { withCredentials: true });
            showNotification('success', `Role "${payload.name}" created successfully!`);
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Error creating role:', error);
            const msg = error.response?.data?.message || 'Server error.';
            showNotification('error', `Failed to create role: ${msg}`);
        } finally {
            setIsSaving(false);
        }
    };

    // ── Colour map per action type ───────────────────────────────────────────
    const actionColors = {
        View: { color: '#10b981', bg: '#f0fdf4', border: '#a7f3d0' },
        Add: { color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
        Edit: { color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
        Delete: { color: '#ef4444', bg: '#fef2f2', border: '#fecaca' }
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div style={{ padding: '24px', minHeight: '100vh', background: '#f8fafc' }}>

            {/* ── Page Header ── */}
            <div style={{ marginBottom: '24px' }}>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>Add New Role</h2>
                <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#94a3b8' }}>
                    Create a new role. Optionally copy permissions from an existing role as a starting template.
                </p>
            </div>

            {/* ── Top Form Card ── */}
            <div style={{
                background: '#fff', borderRadius: '16px',
                border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                padding: '24px', marginBottom: '20px'
            }}>

                {/* Row 1: Permission count + Save/Cancel buttons */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginBottom: '24px', flexWrap: 'wrap', gap: '12px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            background: formData.permissions.length > 0 ? '#f0fdf4' : '#f8fafc',
                            border: `1px solid ${formData.permissions.length > 0 ? '#a7f3d0' : '#e2e8f0'}`,
                            borderRadius: '20px', padding: '6px 14px',
                            fontSize: '13px', fontWeight: 700,
                            color: formData.permissions.length > 0 ? '#10b981' : '#94a3b8',
                            transition: 'all 0.2s'
                        }}>
                            <Check style={{ width: 13, height: 13 }} />
                            {formData.permissions.length} permission{formData.permissions.length !== 1 ? 's' : ''} selected
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {onCancel && (
                            <button
                                onClick={onCancel}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    background: '#f8fafc', color: '#64748b',
                                    border: '1px solid #e2e8f0', borderRadius: '10px',
                                    padding: '10px 18px', fontSize: '14px', fontWeight: 600,
                                    cursor: 'pointer', transition: 'all 0.15s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                                onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
                            >
                                <X style={{ width: 15, height: 15 }} /> Cancel
                            </button>
                        )}
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                background: isSaving ? '#6ee7b7' : '#10b981',
                                color: '#fff', border: 'none', borderRadius: '10px',
                                padding: '10px 22px', fontSize: '14px', fontWeight: 700,
                                cursor: isSaving ? 'not-allowed' : 'pointer',
                                boxShadow: '0 4px 14px rgba(16,185,129,0.2)',
                                transition: 'background 0.2s'
                            }}
                            onMouseEnter={e => { if (!isSaving) e.currentTarget.style.background = '#059669'; }}
                            onMouseLeave={e => { if (!isSaving) e.currentTarget.style.background = '#10b981'; }}
                        >
                            <Save style={{ width: 16, height: 16 }} />
                            {isSaving ? 'Saving…' : 'Save Role'}
                        </button>
                    </div>
                </div>

                {/* Row 2: Copy-from dropdown + Role name input side by side */}
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>

                    {/* ── Copy from existing role dropdown ── */}
                    <div style={{ flex: '1 1 240px', minWidth: '200px' }}>
                        <label style={{
                            display: 'block', fontSize: '11px', fontWeight: 700, color: '#94a3b8',
                            letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px'
                        }}>
                            COPY PERMISSIONS FROM:
                        </label>
                        <div style={{ position: 'relative' }}>
                            <Copy style={{
                                position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)',
                                width: 15, height: 15, color: '#94a3b8', pointerEvents: 'none'
                            }} />
                            <select
                                value={selectedTemplate}
                                onChange={handleTemplateChange}
                                disabled={rolesLoading}
                                style={{
                                    width: '100%', padding: '12px 40px 12px 42px',
                                    border: '1.5px solid #e2e8f0', borderRadius: '12px',
                                    fontSize: '14px', fontWeight: 600, color: selectedTemplate ? '#0f172a' : '#94a3b8',
                                    background: '#f8fafc', outline: 'none', appearance: 'none',
                                    cursor: rolesLoading ? 'wait' : 'pointer', boxSizing: 'border-box',
                                    transition: 'border-color 0.2s'
                                }}
                                onFocus={e => e.target.style.borderColor = '#10b981'}
                                onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                            >
                                <option value="">— Start fresh (no template) —</option>
                                {rolesLoading ? (
                                    <option disabled>Loading roles…</option>
                                ) : existingRoles.length === 0 ? (
                                    <option disabled>No existing roles found</option>
                                ) : (
                                    existingRoles.map(role => (
                                        <option key={role.role_id} value={String(role.role_id)}>
                                            {role.name}
                                            {Array.isArray(role.permissions)
                                                ? ` (${role.permissions.length} permissions)`
                                                : ''}
                                        </option>
                                    ))
                                )}
                            </select>
                            <ChevronDown style={{
                                position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
                                width: 16, height: 16, color: '#94a3b8', pointerEvents: 'none'
                            }} />
                        </div>
                        {selectedTemplate && (
                            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#10b981', fontWeight: 600 }}>
                                ✓ Permissions pre-filled — adjust below as needed
                            </p>
                        )}
                        {!selectedTemplate && !rolesLoading && existingRoles.length > 0 && (
                            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#94a3b8' }}>
                                Select an existing role to copy its permissions as a starting point
                            </p>
                        )}
                    </div>

                    {/* ── New Role Name input ── */}
                    <div style={{ flex: '1 1 240px', minWidth: '200px' }}>
                        <label style={{
                            display: 'block', fontSize: '11px', fontWeight: 700, color: '#94a3b8',
                            letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px'
                        }}>
                            NEW ROLE NAME <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <div style={{ position: 'relative' }}>
                            <Shield style={{
                                position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)',
                                width: 16, height: 16, color: '#94a3b8', pointerEvents: 'none'
                            }} />
                            <input
                                type="text"
                                placeholder="e.g. Manager, Nurse, Receptionist"
                                value={formData.name}
                                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                style={{
                                    width: '100%', padding: '12px 14px 12px 42px',
                                    border: '1.5px solid #e2e8f0', borderRadius: '12px',
                                    fontSize: '14px', fontWeight: 600, color: '#0f172a',
                                    background: '#fff', outline: 'none', boxSizing: 'border-box',
                                    transition: 'border-color 0.2s'
                                }}
                                onFocus={e => e.target.style.borderColor = '#10b981'}
                                onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                            />
                        </div>
                        <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#94a3b8' }}>
                            Enter a unique name for this new role
                        </p>
                    </div>
                </div>

                {/* ── Selected role info banner (when template chosen) ── */}
                {selectedTemplate && (() => {
                    const sourceRole = existingRoles.find(r => String(r.role_id) === String(selectedTemplate));
                    return sourceRole ? (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '12px',
                            background: '#f0fdf4', border: '1px solid #a7f3d0', borderRadius: '12px',
                            padding: '12px 16px', marginBottom: '20px'
                        }}>
                            <Copy style={{ width: 16, height: 16, color: '#10b981', flexShrink: 0 }} />
                            <div>
                                <span style={{ fontSize: '13px', fontWeight: 700, color: '#065f46' }}>
                                    Template: {sourceRole.name}
                                </span>
                                <span style={{ fontSize: '13px', color: '#059669', marginLeft: '8px' }}>
                                    — {formData.permissions.length} permissions loaded. Adjust freely below.
                                </span>
                            </div>
                            <button
                                onClick={() => {
                                    setSelectedTemplate('');
                                    setFormData(prev => ({ ...prev, permissions: [] }));
                                }}
                                title="Clear template"
                                style={{
                                    marginLeft: 'auto', background: 'none', border: 'none',
                                    cursor: 'pointer', color: '#059669', display: 'flex', alignItems: 'center'
                                }}
                            >
                                <X style={{ width: 15, height: 15 }} />
                            </button>
                        </div>
                    ) : null;
                })()}

                {/* ── Quick action buttons ── */}
                <div>
                    <label style={{
                        display: 'block', fontSize: '11px', fontWeight: 700, color: '#94a3b8',
                        letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px'
                    }}>
                        QUICK ACTIONS:
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {['View', 'Add', 'Edit', 'Delete'].map(action => (
                            <button
                                key={action}
                                onClick={() => handleQuickAction(action)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    padding: '7px 14px', fontSize: '13px', fontWeight: 700,
                                    background: actionColors[action].bg, color: actionColors[action].color,
                                    border: `1px solid ${actionColors[action].border}`,
                                    borderRadius: '8px', cursor: 'pointer', transition: 'opacity 0.15s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                            >
                                <Check style={{ width: 13, height: 13 }} /> All {action}
                            </button>
                        ))}
                        <div style={{ width: '1px', background: '#e2e8f0', margin: '0 4px', alignSelf: 'stretch' }} />
                        <button
                            onClick={() => handleQuickAction('Check All')}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '7px 14px', fontSize: '13px', fontWeight: 700,
                                background: '#f0fdf4', color: '#10b981', border: '1px solid #a7f3d0',
                                borderRadius: '8px', cursor: 'pointer', transition: 'opacity 0.15s'
                            }}
                            onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                        >
                            <Check style={{ width: 13, height: 13 }} /> Check All
                        </button>
                        <button
                            onClick={() => handleQuickAction('Uncheck All')}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '7px 14px', fontSize: '13px', fontWeight: 700,
                                background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0',
                                borderRadius: '8px', cursor: 'pointer', transition: 'opacity 0.15s'
                            }}
                            onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                        >
                            <X style={{ width: 13, height: 13 }} /> Uncheck All
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Permission Cards Grid ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingBottom: '32px' }}>
                {permissionStructure.map((category) => (
                    <div
                        key={category.category}
                        style={{
                            background: '#fff', borderRadius: '16px',
                            border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            overflow: 'hidden'
                        }}
                    >
                        {/* Category header */}
                        <div style={{ padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                            <h4 style={{
                                margin: 0, fontSize: '11px', fontWeight: 800, color: '#1e293b',
                                letterSpacing: '0.15em', textTransform: 'uppercase'
                            }}>
                                {category.category}
                            </h4>
                        </div>

                        {/* Subsections */}
                        <div>
                            {category.subsections.map((sub, subIdx) => (
                                <div
                                    key={sub.name}
                                    style={{
                                        padding: '16px 20px',
                                        borderBottom: subIdx < category.subsections.length - 1 ? '1px solid #f8fafc' : 'none',
                                        display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap'
                                    }}
                                >
                                    <span style={{
                                        fontSize: '11px', fontWeight: 700, color: '#10b981',
                                        letterSpacing: '0.08em', textTransform: 'uppercase', minWidth: '200px'
                                    }}>
                                        {sub.name}
                                    </span>
                                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                        {sub.actions.map((action) => {
                                            const permKey = `${sub.key} ${action}`;
                                            const isChecked = formData.permissions.includes(permKey);
                                            const ac = actionColors[action] || actionColors.View;
                                            return (
                                                <label
                                                    key={action}
                                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}
                                                >
                                                    <div
                                                        onClick={() => togglePermission(permKey)}
                                                        style={{
                                                            width: '20px', height: '20px', borderRadius: '6px',
                                                            border: `2px solid ${isChecked ? ac.color : '#d1d5db'}`,
                                                            background: isChecked ? ac.color : '#fff',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            transition: 'all 0.15s', flexShrink: 0, cursor: 'pointer'
                                                        }}
                                                    >
                                                        {isChecked && (
                                                            <Check style={{ width: 12, height: 12, color: '#fff', strokeWidth: 3 }} />
                                                        )}
                                                    </div>
                                                    <span style={{
                                                        fontSize: '13px',
                                                        fontWeight: isChecked ? 700 : 500,
                                                        color: isChecked ? '#1e293b' : '#94a3b8',
                                                        transition: 'color 0.15s'
                                                    }}>
                                                        {action}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AddRole;
